import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Form, Button, Card, Modal } from "react-bootstrap";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import * as htmlToImage from "html-to-image";

const sampleSchoolLogo = `${process.env.PUBLIC_URL}/assets/sampleSchoolLogo.png`;

/* ---------------- DOB IN WORDS (AUTO) ---------------- */
const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const numberToWordsUnder100 = (n) => {
  const ones = [
    "Zero",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  if (n < 20) return ones[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return o === 0 ? tens[t] : `${tens[t]} ${ones[o]}`;
};

const numberToWordsUnder1000 = (n) => {
  if (n < 100) return numberToWordsUnder100(n);
  const h = Math.floor(n / 100);
  const r = n % 100;
  if (r === 0) return `${numberToWordsUnder100(h)} Hundred`;
  return `${numberToWordsUnder100(h)} Hundred ${numberToWordsUnder100(r)}`;
};

const yearToWords = (year) => {
  const y = Number(year);
  if (!y || y < 1) return "";
  // Simple + readable year reading
  // 2002 => Two Thousand Two
  // 2018 => Two Thousand Eighteen
  // 1998 => One Thousand Nine Hundred Ninety Eight
  if (y >= 2000 && y < 2100) {
    const rest = y - 2000;
    return rest === 0 ? "Two Thousand" : `Two Thousand ${numberToWordsUnder1000(rest)}`;
  }
  if (y >= 1000 && y < 2000) {
    const first = Math.floor(y / 100);
    const rest = y % 100;
    const firstWords = numberToWordsUnder100(first);
    if (rest === 0) return `${firstWords} Hundred`;
    return `${firstWords} Hundred ${numberToWordsUnder100(rest)}`;
  }
  return numberToWordsUnder1000(y);
};

const parseDobFlexible = (s) => {
  const v = (s ?? "").toString().trim();
  if (!v) return null;

  // yyyy-mm-dd
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    if (y && m >= 1 && m <= 12 && d >= 1 && d <= 31) return { d, m, y };
  }

  // dd-mm-yyyy or dd/mm/yyyy or dd.mm.yyyy
  const dmy = v.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (dmy) {
    const d = Number(dmy[1]);
    const m = Number(dmy[2]);
    const y = Number(dmy[3]);
    if (y && m >= 1 && m <= 12 && d >= 1 && d <= 31) return { d, m, y };
  }

  // fallback: try Date()
  const dt = new Date(v);
  if (!Number.isNaN(dt.getTime())) {
    return { d: dt.getDate(), m: dt.getMonth() + 1, y: dt.getFullYear() };
  }

  return null;
};

const dobToWords = (dobStr) => {
  const parts = parseDobFlexible(dobStr);
  if (!parts) return "";
  const { d, m, y } = parts;

  const dayWords = numberToWordsUnder100(d);
  const monthWord = monthNames[m - 1] || "";
  const yearWords = yearToWords(y);

  if (!dayWords || !monthWord || !yearWords) return "";
  // Example: "Sixteen November Two Thousand Two"
  return `${dayWords} ${monthWord} ${yearWords}`.replace(/\s+/g, " ").trim();
};

const formatDateToDMY = (s) => {
  const parts = parseDobFlexible(s);
  if (!parts) return s || "";
  const { d, m, y } = parts;
  const dd = String(d).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  return `${dd}-${mm}-${y}`;
};

const StudyCertificateGenerator = () => {
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [school, setSchool] = useState(null);

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCertSection, setShowCertSection] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // ✅ track if user manually edited DOB in words (so we don't overwrite)
  const [dobWordsTouched, setDobWordsTouched] = useState(false);

  const [form, setForm] = useState({
    class_id: "",
    section_id: "",
    student_id: "",
  });

  const [cert, setCert] = useState({
    admission_no: "",
    date: new Date().toISOString().slice(0, 10), // yyyy-mm-dd
    prefix_master_kumari: "Master",
    son_daughter: "Son",
    father_name: "",
    studied_class_from: "",
    studied_class_to: "",
    year_from: "",
    year_to: "",
    dob: "",
    dob_in_words: "",
    character_found: "",
  });

  const certRef = useRef(null);

  const encodePath = (p = "") =>
    String(p)
      .replace(/^\/+/, "")
      .split("/")
      .map(encodeURIComponent)
      .join("/");

  const normalizeUrl = (pathOrUrl) => {
    if (!pathOrUrl) return "";
    const v = String(pathOrUrl).trim();
    if (!v) return "";
    if (v.startsWith("http://") || v.startsWith("https://") || v.startsWith("data:")) return v;
    const clean = v.replace(/^\/+/, "");
    return `${baseUrl}/uploads/get-image/${encodePath(clean)}`;
  };

  const logoUrlFromSession = userObj.logo ? normalizeUrl(userObj.logo) : sampleSchoolLogo;

  const toBase64FromUrl = async (url) => {
    const res = await axios.get(url, { responseType: "blob" });
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(res.data);
    });
  };

  const waitForImages = async (rootEl) => {
    if (!rootEl) return;
    const imgs = Array.from(rootEl.querySelectorAll("img"));
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete && img.naturalWidth > 0) return resolve(true);
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
          })
      )
    );
  };

  useEffect(() => {
    fetchClasses();
    fetchSchool();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (form.class_id) fetchSections(form.class_id);

    setForm((p) => ({ ...p, section_id: "", student_id: "" }));
    setSections([]);
    setStudents([]);
    setSelectedStudent(null);
    setShowCertSection(false);
    // eslint-disable-next-line
  }, [form.class_id]);

  useEffect(() => {
    if (form.class_id && form.section_id) fetchStudents(form.class_id, form.section_id);

    setForm((p) => ({ ...p, student_id: "" }));
    setStudents([]);
    setSelectedStudent(null);
    setShowCertSection(false);
    // eslint-disable-next-line
  }, [form.section_id]);

  // ✅ AUTO-FILL DOB IN WORDS whenever dob changes (unless user manually edited)
  useEffect(() => {
    if (dobWordsTouched) return;
    const words = dobToWords(cert.dob);
    setCert((p) => ({ ...p, dob_in_words: words }));
    // eslint-disable-next-line
  }, [cert.dob, dobWordsTouched]);

  const fetchClasses = async () => {
    try {
      const res = await axios.post(baseUrl + "/classes/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      setClasses(res.data || []);
    } catch (err) {
      console.error("Error fetching classes:", err);
    }
  };

  const fetchSections = async (class_id) => {
    try {
      const res = await axios.post(baseUrl + "/Sections/", {
        action: "DROPDOWNREAD",
        class_id,
        school_id: userObj.school_id,
        academic_year_id: userObj.academic_year_id,
      });
      setSections(res.data || []);
    } catch (err) {
      console.error("Error fetching sections:", err);
    }
  };

  const fetchSchool = async () => {
    try {
      const res = await axios.post(baseUrl + "/schoolmaster/", {
        action: "FILTER",
        school_id: userObj.school_id,
      });

      const row = Array.isArray(res.data)
        ? res.data.find((x) => String(x.school_id) === String(userObj.school_id)) || null
        : null;

      let finalLogo = logoUrlFromSession;
      try {
        if (finalLogo && finalLogo !== sampleSchoolLogo && !finalLogo.startsWith("data:")) {
          finalLogo = await toBase64FromUrl(finalLogo);
        }
      } catch (e) {
        console.warn("Logo base64 conversion failed:", e);
        finalLogo = logoUrlFromSession;
      }

      setSchool({
        school_name: row?.school_name || userObj.school_name || "",
        logo: finalLogo,
      });
    } catch (err) {
      console.error("School fetch error:", err);
      setSchool({
        school_name: userObj.school_name || "",
        logo: logoUrlFromSession,
      });
    }
  };

  const fetchStudents = async (class_id, section_id) => {
    try {
      setLoading(true);
      const res = await axios.post(baseUrl + "/students/", {
        action: "FILTER",
        school_id: userObj.school_id,
        academic_year_id: userObj.academic_year_id,
        class_id,
        section_id,
      });

      const data = Array.isArray(res.data) ? res.data : [];
      setStudents(data);
    } catch (err) {
      console.error("Students fetch error:", err);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const studentOptions = useMemo(() => {
    return (students || []).map((st) => ({
      value: st.student_id,
      label: `${st.student_first_name || ""} ${st.student_last_name || ""}`.trim(),
      data: st,
    }));
  }, [students]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((p) => ({ ...p, [id]: value ?? "" }));
  };

  const handleCertChange = (e) => {
    const { id, value } = e.target;

    // ✅ if user manually edits dob_in_words, stop auto overwrite
    if (id === "dob_in_words") setDobWordsTouched(true);

    // ✅ if user edits dob, allow auto to update words again
    if (id === "dob") setDobWordsTouched(false);

    setCert((p) => ({ ...p, [id]: value ?? "" }));
  };

  const handleStudentSelect = (opt) => {
    if (!opt) {
      setForm((p) => ({ ...p, student_id: "" }));
      setSelectedStudent(null);
      setShowCertSection(false);
      return;
    }

    setForm((p) => ({ ...p, student_id: opt.value }));

    const st = opt.data || {};
    const fullName = `${st.student_first_name || ""} ${st.student_last_name || ""}`.trim();

    // Look up class name from ID
    const admissionClass = classes.find((c) => String(c.class_id) === String(st.admission_to));
    const admissionClassName = admissionClass ? admissionClass.class_name : "";

    setSelectedStudent({
      student_id: st.student_id,
      student_name: fullName,
      class_name: st.class_name || "",
      section_name: st.section_name || "",
      admission_number: st.admission_number || "",
      dob: st.date_of_birth || st.dob || "",
      father_name: `${st.father_surname || ""} ${st.father_firstname || ""}`.trim(),
      gender: (st.gender || "").toLowerCase(),
    });

    // reset touch so auto words works for selected student DOB
    setDobWordsTouched(false);

    setCert((p) => {
      const nextDob = formatDateToDMY(st.date_of_birth || st.dob || p.dob);
      return {
        ...p,
        admission_no: st.admission_number || p.admission_no,
        father_name: `${st.father_surname || ""} ${st.father_firstname || ""}`.trim() || p.father_name,
        dob: nextDob,
        dob_in_words: dobToWords(nextDob) || p.dob_in_words,
        prefix_master_kumari: (st.gender || "").toLowerCase() === "female" ? "Kumari" : "Master",
        son_daughter: (st.gender || "").toLowerCase() === "female" ? "Daughter" : "Son",
        studied_class_to: st.class_name || p.studied_class_to,
        studied_class_from: admissionClassName || p.studied_class_from || "",
        year_from: p.year_from || "",
        year_to: p.year_to || "",
      };
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!form.class_id) return toast.error("Class is required");
    if (!form.section_id) return toast.error("Section is required");
    if (!form.student_id) return toast.error("Student is required");
    setShowCertSection(true);
  };

  const handleReset = () => {
    setForm({ class_id: "", section_id: "", student_id: "" });
    setSections([]);
    setStudents([]);
    setSelectedStudent(null);
    setShowCertSection(false);
    setDobWordsTouched(false);
    setCert({
      admission_no: "",
      date: new Date().toISOString().slice(0, 10),
      prefix_master_kumari: "Master",
      son_daughter: "Son",
      father_name: "",
      studied_class_from: "",
      studied_class_to: "",
      year_from: "",
      year_to: "",
      dob: "",
      dob_in_words: "",
      character_found: "",
    });
  };

  const downloadPng = async () => {
    try {
      await waitForImages(certRef.current);

      const dataUrl = await htmlToImage.toPng(certRef.current, {
        cacheBust: true,
        pixelRatio: 3,
        backgroundColor: "#ffffff",
      });

      const link = document.createElement("a");
      link.download = `${(selectedStudent?.student_name || "STUDY_CERTIFICATE").replaceAll(" ", "_")}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("downloadPng error:", err);
      toast.error("Download failed. Check image URL / CORS.");
    }
  };

  // ✅ PRINT FULL A4 (Landscape) + COVER WHOLE SHEET
  const handlePrint = async () => {
    try {
      if (!certRef.current) return toast.error("Certificate not ready");
      await waitForImages(certRef.current);

      const html = certRef.current.outerHTML;

      const w = window.open("", "_blank", "width=1100,height=700");
      if (!w) return toast.error("Popup blocked. Allow popups for printing.");

      w.document.open();
      w.document.write(`
        <html>
          <head>
            <title>Study Certificate</title>
            <meta charset="utf-8" />
            <style>
              @page { size: A4 landscape; margin: 0; }
              html, body { width: 100%; height: 100%; margin: 0; padding: 0; }
              body { background: #fff; overflow: hidden; }
              .printWrap{
                width: 297mm;
                height: 210mm;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #fff;
              }
              .printWrap > *{
                width: 297mm !important;
                height: 210mm !important;
                box-sizing: border-box;
              }
              *{ -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            </style>
          </head>
          <body>
            <div class="printWrap">${html}</div>
            <script>
              window.onload = function(){
                window.focus();
                window.print();
                setTimeout(()=>window.close(), 300);
              }
            </script>
          </body>
        </html>
      `);
      w.document.close();
    } catch (e) {
      console.error("print error:", e);
      toast.error("Print failed");
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body className="hide-scrollbar">
            <form onSubmit={handleSearch}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">Study Certificate Generator</h6>
                </Col>
              </Row>

              <Row className="g-3">
                <Col xs={12} md={6} lg={3}>
                  <Form.Group className="commonInput">
                    <Form.Label>
                      Class<span className="requiredStar">*</span>
                    </Form.Label>
                    <Form.Select id="class_id" value={form.class_id} onChange={handleInputChange} required>
                      <option value="" disabled hidden>
                        Select Class
                      </option>
                      {classes.map((cls) => (
                        <option key={cls.class_id} value={cls.class_id}>
                          {cls.class_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={3}>
                  <Form.Group className="commonInput">
                    <Form.Label>
                      Section<span className="requiredStar">*</span>
                    </Form.Label>
                    <Form.Select
                      id="section_id"
                      value={form.section_id}
                      onChange={handleInputChange}
                      required
                      disabled={!form.class_id}
                    >
                      <option value="" disabled hidden>
                        Select Section
                      </option>
                      {sections.map((sec) => (
                        <option key={sec.section_id} value={sec.section_id}>
                          {sec.section_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={12} lg={4}>
                  <Form.Group className="commonInput">
                    <Form.Label>
                      Student<span className="requiredStar">*</span>
                    </Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      isClearable
                      isSearchable
                      isLoading={loading}
                      placeholder={form.section_id ? "Search student..." : "Select Class & Section first"}
                      options={studentOptions}
                      onChange={handleStudentSelect}
                      value={studentOptions.find((o) => String(o.value) === String(form.student_id)) || null}
                      isDisabled={!form.section_id}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldScrollIntoView={false}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} lg={2}>
                  <div className="d-flex justify-content-end mt-4">
                    <Button type="button" variant="primary" className="btn-info clearBtn me-3" onClick={handleReset}>
                      Reset
                    </Button>
                    <Button type="submit" variant="primary" className="btn-success primaryBtn">
                      Search
                    </Button>
                  </div>
                </Col>
              </Row>
            </form>
          </Card.Body>
        </Card>

        {showCertSection && (
          <Row className="mt-3 g-3">
            <Col xs={12} lg={5}>
              <Card>
                <Card.Body className="hide-scrollbar">
                  <h6 className="commonSectionTitle">Certificate Details</h6>
                  <hr />

                  <Row className="g-2">
                    <Col xs={12} md={6}>
                      <Form.Group className="commonInput">
                        <Form.Label>Admission No</Form.Label>
                        <Form.Control id="admission_no" value={cert.admission_no} onChange={handleCertChange} />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group className="commonInput">
                        <Form.Label>Date</Form.Label>
                        <Form.Control id="date" type="date" value={cert.date} onChange={handleCertChange} />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group className="commonInput">
                        <Form.Label>Master / Kumari</Form.Label>
                        <Form.Select id="prefix_master_kumari" value={cert.prefix_master_kumari} onChange={handleCertChange}>
                          <option value="Master">Master</option>
                          <option value="Kumari">Kumari</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group className="commonInput">
                        <Form.Label>Son / Daughter</Form.Label>
                        <Form.Select id="son_daughter" value={cert.son_daughter} onChange={handleCertChange}>
                          <option value="Son">Son</option>
                          <option value="Daughter">Daughter</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group className="commonInput">
                        <Form.Label>Father Name (Sri.)</Form.Label>
                        <Form.Control id="father_name" value={cert.father_name} onChange={handleCertChange} />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group className="commonInput">
                        <Form.Label>Class From</Form.Label>
                        <Form.Control id="studied_class_from" value={cert.studied_class_from} onChange={handleCertChange} />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group className="commonInput">
                        <Form.Label>Class To</Form.Label>
                        <Form.Control id="studied_class_to" value={cert.studied_class_to} onChange={handleCertChange} />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group className="commonInput">
                        <Form.Label>Year From</Form.Label>
                        <Form.Control id="year_from" value={cert.year_from} onChange={handleCertChange} placeholder="e.g. 2024" />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group className="commonInput">
                        <Form.Label>Year To</Form.Label>
                        <Form.Control id="year_to" value={cert.year_to} onChange={handleCertChange} placeholder="e.g. 2025" />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group className="commonInput">
                        <Form.Label>Date of Birth</Form.Label>
                        <Form.Control id="dob" value={cert.dob} onChange={handleCertChange} placeholder="DD-MM-YYYY / YYYY-MM-DD" />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group className="commonInput">
                        <Form.Label>DOB (In words) (Auto)</Form.Label>
                        <Form.Control
                          id="dob_in_words"
                          value={cert.dob_in_words}
                          onChange={handleCertChange}
                          placeholder="Auto generated"
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group className="commonInput">
                        <Form.Label>Character found</Form.Label>
                        <Form.Control
                          id="character_found"
                          value={cert.character_found}
                          onChange={handleCertChange}
                          placeholder="Good / Very Good"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end mt-4">
                    <Button variant="secondary" className="me-2" onClick={() => setIsFullScreen(true)}>
                      Full Screen
                    </Button>
                    <Button variant="outline-dark" className="me-2" onClick={handlePrint}>
                      Print
                    </Button>
                    <Button variant="success" className="primaryBtn" onClick={downloadPng}>
                      Download PNG
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} lg={7}>
              <Card>
                <Card.Body className="hide-scrollbar">
                  <h6 className="commonSectionTitle">Preview</h6>
                  <div style={{ display: "flex", justifyContent: "center", padding: 14 }}>
                    <StudyCertificate ref={certRef} school={school} student={selectedStudent} cert={cert} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>

      <Modal show={isFullScreen} onHide={() => setIsFullScreen(false)} fullscreen>
        <Modal.Header closeButton>
          <Modal.Title>Study Certificate - Full Screen</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <StudyCertificate school={school} student={selectedStudent} cert={cert} />
            </div>
            <div className="d-flex justify-content-center mt-3">
              <Button variant="outline-dark" className="me-2" onClick={handlePrint}>
                Print
              </Button>
              <Button variant="success" onClick={downloadPng}>
                Download PNG
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default StudyCertificateGenerator;

/* ---------------- CERTIFICATE (NEAT DIAMOND BORDER + CLEAN) ---------------- */

const makeDiamondBorderDataUri = (stroke = "#2b2b2b") => {
  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160">
    <rect x="0" y="0" width="160" height="160" fill="none"/>
    <g fill="${stroke}">
      ${Array.from({ length: 18 })
        .map((_, i) => {
          const x = 8 + i * 8;
          return `<path d="M ${x} 6 L ${x + 3} 9 L ${x} 12 L ${x - 3} 9 Z"/>`;
        })
        .join("")}

      ${Array.from({ length: 18 })
        .map((_, i) => {
          const x = 8 + i * 8;
          return `<path d="M ${x} 154 L ${x + 3} 151 L ${x} 148 L ${x - 3} 151 Z"/>`;
        })
        .join("")}

      ${Array.from({ length: 16 })
        .map((_, i) => {
          const y = 14 + i * 8;
          return `<path d="M 6 ${y} L 9 ${y + 3} L 12 ${y} L 9 ${y - 3} Z"/>`;
        })
        .join("")}

      ${Array.from({ length: 16 })
        .map((_, i) => {
          const y = 14 + i * 8;
          return `<path d="M 154 ${y} L 151 ${y + 3} L 148 ${y} L 151 ${y - 3} Z"/>`;
        })
        .join("")}
    </g>
  </svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const StudyCertificate = React.forwardRef(({ school, student, cert }, ref) => {
  const s = school || {};
  const st = student || {};
  const c = cert || {};
  const safe = (v) => (v ?? "").toString();

  const schoolName = safe(s.school_name);
  const studentName = safe(st.student_name);
  const classText = safe(st.class_name);
  const admissionNo = safe(c.admission_no || st.admission_number);

  return (
    <div ref={ref} style={scStyles.sheet}>
      <div style={scStyles.borderOuter}>
        <div style={scStyles.borderInner}>
          <div style={scStyles.header}>
            <div style={scStyles.logoWrap}>
              {safe(s.logo) ? <img src={safe(s.logo)} alt="logo" style={scStyles.logo} /> : null}
              <div style={scStyles.estText}>Estd. : 1983 - 84</div>
            </div>

            <div style={scStyles.headerCenter}>
              <div style={scStyles.schoolTitle}>{schoolName || "SCHOOL NAME"}</div>
              <div style={scStyles.certTitle}>Bonafide And Conduct Certificate</div>
            </div>
          </div>

          {/* ✅ REMOVE DOT LEADERS ONLY FOR Admission No & Date (as you asked) */}
          <div style={scStyles.rowBetween}>
            <div style={scStyles.smallLineNoDots}>
              <span style={scStyles.label}>Admission No.</span> :{" "}
              <span style={scStyles.underlineFillSmall}>{admissionNo}</span>
            </div>

            <div style={scStyles.smallLineNoDots}>
              <span style={scStyles.label}>Date</span> :{" "}
              <span style={scStyles.underlineFillSmall}>{formatDateToDMY(c.date)}</span>
            </div>
          </div>

          <div style={scStyles.body}>
            <LineText>
              This is to certify that {safe(c.prefix_master_kumari)}{" "}
              <span style={scStyles.underlineFillWideName}>{studentName}</span>.
            </LineText>

            <LineText>
              Son / Daughter of sri. <span style={scStyles.underlineFillWide}>{safe(c.father_name)}</span>
            </LineText>

            <LineText>
              was / is a bonafide student of this school studied / studying in class from{" "}
              <span style={scStyles.underlineFill}>{safe(c.studied_class_from)}</span> to{" "}
              <span style={scStyles.underlineFill}>{safe(c.studied_class_to || classText)}</span> during the year(s) from{" "}
              <span style={scStyles.underlineFill}>{safe(c.year_from)}</span> to{" "}
              <span style={scStyles.underlineFill}>{safe(c.year_to)}</span>.
            </LineText>

            <LineText>
              His / Her date of Birth is as per the Records is{" "}
              <span style={scStyles.underlineFillWide}>{formatDateToDMY(c.dob)}</span>
            </LineText>

            <LineText>
              (In words <span style={scStyles.underlineFillUltraWide}>{safe(c.dob_in_words)}</span> )
            </LineText>

            <LineText>
              His / Her Character is / was found{" "}
              <span style={scStyles.underlineFillWide}>{safe(c.character_found)}</span>
            </LineText>
          </div>

          <div style={scStyles.footer}>
            <div style={scStyles.headMasterBox}>
              <div style={scStyles.signLine} />
              <div style={scStyles.headMasterText}>HEADMASTER</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

function LineText({ children }) {
  return <div style={scStyles.line}>{children}</div>;
}

const scStyles = {
  sheet: {
    width: 820,
    maxWidth: "100%",
    background: "#ffffff",
    color: "#222",
    padding: 24,
  },

  borderOuter: {
    background: "#ffffff",
    border: "5px solid #2b2b2b",
    padding: 8,
  },

  borderInner: {
    padding: 18,
    position: "relative",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontVariantNumeric: "lining-nums",
  },

  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    paddingBottom: 6,
  },
  logoWrap: {
    width: 120,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    paddingTop: 2,
  },
  logo: {
    width: 72,
    height: 72,
    objectFit: "contain",
  },
  estText: {
    fontSize: 12,
    fontWeight: 600,
    opacity: 0.85,
  },

  headerCenter: {
    flex: 1,
    textAlign: "center",
    paddingRight: 90,
  },


  schoolTitle: {
    fontSize: 34,
    fontWeight: 900,
    letterSpacing: 1,
    textTransform: "uppercase",
    fontFamily: "Impact, Haettenschweiler, 'Arial Black', 'Franklin Gothic Medium', Arial, sans-serif",
  },

  // ✅ closer to photo: script cursive
  certTitle: {
    marginTop: 2,
    fontSize: 30,
    fontStyle: "italic",
    fontWeight: 700,
    fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Segoe Script', cursive",
  },

  rowBetween: {
    marginTop: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 16,
  },

  label: {
    fontWeight: 700,
  },

  // ✅ for admission/date without dot leaders
  
  body: {
    marginTop: 14,
  },

  // ✅ closer to picture: italic body text + tighter
  line: {
    fontSize: 18,
    lineHeight: "30px",
    marginBottom: 6,
    fontStyle: "italic",
  },

  underlineFill: {
    display: "inline-block",
    minWidth: 120,
    padding: "0 6px",
    borderBottom: "1px solid #333",
    fontWeight: 700,
    fontStyle: "normal",
  },

  underlineFillSmall: {
    display: "inline-block",
    minWidth: 170,
    padding: "0 6px",
    borderBottom: "1px solid #333",
    fontWeight: 700,
    fontStyle: "normal",
  },

  underlineFillWide: {
    display: "inline-block",
    minWidth: 260,
    padding: "0 6px",
    borderBottom: "1px solid #333",
    fontWeight: 700,
    fontStyle: "normal",
  },

  underlineFillWideName: {
    display: "inline-block",
    minWidth: 360,
    padding: "0 6px",
    borderBottom: "1px solid #333",
    fontWeight: 700,
    fontStyle: "normal",
  },

  underlineFillUltraWide: {
    display: "inline-block",
    minWidth: 520,
    padding: "0 6px",
    borderBottom: "1px solid #333",
    fontWeight: 700,
    fontStyle: "normal",
  },

  footer: {
    marginTop: 18,
    display: "flex",
    justifyContent: "flex-end",
  },
  headMasterBox: {
    width: 260,
    textAlign: "center",
  },
  signLine: {
    height: 1,
    background: "#222",
    marginBottom: 8,
  },
  headMasterText: {
    fontSize: 16,
    fontWeight: 800,
    letterSpacing: 1,
  },
};
